import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';

import { CACHE_EXPIRATION_TIME_REFERENCE_BOOK } from '@/features/constants';
import { FiltersGrid, Pagination } from '@/features/types';
import { infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { CounterpartyPosition, CounterpartyPositionRequestBody } from './types';

export const counterpartyPositionsApi = createApi({
  reducerPath: 'counterpartyPositionsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/counterparty-positions`,
  }),
  tagTypes: ['CounterpartyPosition', 'CounterpartyPositions'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME_REFERENCE_BOOK,
  endpoints: builder => ({
    getCounterpartyPositionsTypes: builder.query<CounterpartyPosition[], void>({
      query: () => ({
        url: '/',
      }),
    }),
    getCounterpartyPositions: builder.query<CounterpartyPosition[], void>({
      query: () => ({
        url: '/',
      }),
      providesTags: () => ['CounterpartyPosition', 'CounterpartyPositions'],
    }),
    getCounterpartyPositionsI: builder.infiniteQuery<
      CounterpartyPosition[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/query`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['CounterpartyPosition', 'CounterpartyPositions'],
    }),
    getCounterpartyPositionById: builder.query<CounterpartyPosition, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['CounterpartyPosition'],
    }),
    createCounterpartyPosition: builder.mutation<
      CounterpartyPosition,
      CounterpartyPositionRequestBody
    >({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: result => [
        'CounterpartyPosition',
        { type: 'CounterpartyPosition', id: result?.id },
      ],
    }),
    updateCounterpartyPosition: builder.mutation<
      CounterpartyPosition,
      CounterpartyPositionRequestBody
    >({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        'CounterpartyPosition',
        { type: 'CounterpartyPosition', id: result?.id },
      ],
    }),
    deleteCounterpartyPositionById: builder.mutation<void, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => [
        'CounterpartyPosition',
        { type: 'CounterpartyPosition', id },
      ],
    }),
  }),
});

export const {
  useGetCounterpartyPositionsTypesQuery,
  useGetCounterpartyPositionsQuery,
  useCreateCounterpartyPositionMutation,
  useUpdateCounterpartyPositionMutation,
  useDeleteCounterpartyPositionByIdMutation,
} = counterpartyPositionsApi;

export const useGetCounterpartyPositionsIInfiniteQuery =
  normalizeInfiniteQueryData(
    counterpartyPositionsApi.useGetCounterpartyPositionsIInfiniteQuery,
  );
